import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface UseTodoListReviewersReturn {
  /** Principal ids currently designated as reviewers of this list. */
  reviewerPrincipalIds: Set<string>;
  isLoading: boolean;
  /** Replace the list's reviewer set (owner/admin only, enforced server-side). */
  setReviewers: (principalIds: string[]) => Promise<void>;
  isSaving: boolean;
}

/**
 * The reviewer set for a shared list. Reviewers are a per-list designation
 * (NOT a Vault role) — a subset of the workspace's members who may Verify /
 * Send-back in-review items and are notified when a task enters review.
 *
 * Only meaningful on Org (shared) Holders; pass `enabled=false` on Personal
 * lists (no members) to skip the fetch.
 */
export function useTodoListReviewers(
  holderId: string,
  listId: string,
  enabled = true,
): UseTodoListReviewersReturn {
  const queryClient = useQueryClient();
  const key = queryKeys.todoLists.reviewers(holderId, listId);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => api.listTodoListReviewers(holderId, listId),
    enabled: enabled && !!holderId && !!listId,
    staleTime: 60_000,
  });

  const reviewerPrincipalIds = useMemo(
    () => new Set((data?.reviewers ?? []).map((r) => r.principalId)),
    [data],
  );

  const mutation = useMutation({
    mutationFn: (principalIds: string[]) =>
      api.setTodoListReviewers(holderId, listId, principalIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  return {
    reviewerPrincipalIds,
    isLoading: enabled && isLoading,
    setReviewers: async (principalIds: string[]) => {
      await mutation.mutateAsync(principalIds);
    },
    isSaving: mutation.isPending,
  };
}
